import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SolicitudesRadicadasService } from '../../services/radicacion-traslados-service/solicitudes-radicadas.service';
import {
  CatalogoTiposNovedadQuery,
  TiposNovedadService,
  TipoNovedadDTO,
} from '../../services/radicacion-traslados-service/tipos-novedad.service';
import {
  SubtiposNovedadService,
  SubtiposNovedadDTO,
  SubtipoDTO,
} from '../../services/radicacion-traslados-service/subtipos-novedad.service';
import { TipoSolicitudNovedadTrasladoIdService } from '../../services/radicacion-traslados-service/tipo-solicitud-novedad-traslado-id.service';
import { environment } from '../../../environments/environment';

/** Misma convención que paso 2 (`tipo-novedad-selector`). */
const ESTADO_ACTIVO = 'ACTIVO';

@Component({
  selector: 'app-historico-traslados',
  templateUrl: './historico-traslados.component.html',
  styleUrls: ['./historico-traslados.component.css'],
})
export class HistoricoTrasladosComponent implements OnInit {
  @Input() numeroContrato: string = '';
  /** Estado del contrato (Afilmed) para `contratoTransitorio` en catálogos, igual que paso 2. */
  @Input() estadoContrato: string = '';

  public filtrosBusqueda: FormGroup = new FormGroup({});

  attrData: string[] = [
    'no',
    'nroSolicitud',
    'numeroContrato',
    'tipoNovedad',
    'subtipoNovedad',
    'fechaCreacion',
    'fechaUltimaGestion',
    'creadoPor',
    'estado',
  ];

  namesColumns: string[] = [
    'No.',
    'Nro. solicitud traslado',
    'Número contrato',
    'Tipo de novedad',
    'Subtipo de novedad',
    'Fecha de creación',
    'Fecha última gestión',
    'Creado por',
    'Estado',
  ];

  tableData: any[] = [];
  isLoading = false;
  errorMsg: string | null = null;

  private solicitudesOriginales: any[] = [];

  tiposNovedad: string[] = [];
  subtiposNovedad: string[] = [];

  /** Catálogo tipos (codigo = idTipoNovedad) alineado con paso 2. */
  private tiposNovedadCatalog: { codigo: string; nombre: string }[] = [];
  private catalogoQuery: CatalogoTiposNovedadQuery | null = null;
  private allSubtiposCatalog: SubtiposNovedadDTO[] = [];

  constructor(
    private fb: FormBuilder,
    private solicitudesService: SolicitudesRadicadasService,
    private tiposNovedadService: TiposNovedadService,
    private subtiposNovedadService: SubtiposNovedadService,
    private tipoSolicitudNovedadTrasladoIdService: TipoSolicitudNovedadTrasladoIdService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadCatalogosTiposYSubtipos();
    this.cargarSolicitudes();
    this.filtrosBusqueda
      .get('tipoNovedadTraslado')
      ?.valueChanges.subscribe((selectedTipos: string[]) => {
        this.updateSubtiposFromSelection(selectedTipos || []);
      });
  }

  cargarSolicitudes(): void {
    if (!this.numeroContrato) {
      return;
    }
    this.isLoading = true;
    this.errorMsg = null;
    this.solicitudesService
      .getSolicitudesRadicadas(this.numeroContrato)
      .subscribe({
        next: (res) => {
          this.solicitudesOriginales = res.map((item, index) => ({
            no: index + 1,
            nroSolicitud: item.idSolicitudesRadicadas,
            numeroContrato: item.numeroContrato,
            tipoNovedad: item.tipoNovedad,
            subtipoNovedad: item.subtipoNovedad,
            fechaCreacion: item.fechaCreacion,
            fechaUltimaGestion: item.fechaUltimaGestion ?? '-',
            creadoPor: item.creadoPor,
            estado: item.estado,
          }));
          this.tableData = [...this.solicitudesOriginales];
          this.isLoading = false;
        },
        error: () => {
          this.errorMsg =
            'No se pudieron cargar las solicitudes. Intente nuevamente.';
          this.isLoading = false;
        },
      });
  }

  /**
   * Misma secuencia que paso 2 (`loadTiposNovedad` + catálogo anidado de subtipos):
   * GET tipos-novedad y GET subtipos-novedad con el mismo `CatalogoTiposNovedadQuery`.
   */
  private loadCatalogosTiposYSubtipos(): void {
    this.tiposNovedad = [];
    this.tiposNovedadCatalog = [];
    this.allSubtiposCatalog = [];
    this.catalogoQuery = null;

    this.resolveCatalogoQuery$()
      .pipe(
        tap((qCat) => {
          this.catalogoQuery = qCat;
        }),
        switchMap((qCat) =>
          forkJoin({
            tipos: this.tiposNovedadService.getTiposNovedad(qCat),
            subtipos: this.subtiposNovedadService.getSubtiposNovedad(qCat),
          }),
        ),
      )
      .subscribe({
        next: ({ tipos, subtipos }) => {
          try {
            this.tiposNovedadCatalog = (tipos || [])
              .map((dto) => ({
                codigo: String(dto.idTipoNovedad),
                nombre: String(dto.tipoNovedad ?? '').trim(),
                estado:
                  dto.estado === 1 || dto.estado === '1'
                    ? ESTADO_ACTIVO
                    : 'INACTIVO',
              }))
              .filter((t) => t.estado === ESTADO_ACTIVO && t.nombre.length > 0)
              .sort((a, b) => Number(a.codigo) - Number(b.codigo));

            this.tiposNovedad = this.tiposNovedadCatalog.map((t) => t.nombre);
            this.allSubtiposCatalog = Array.isArray(subtipos) ? subtipos : [];

            const selectedTipos =
              this.filtrosBusqueda.get('tipoNovedadTraslado')?.value || [];
            if (selectedTipos.length) {
              this.updateSubtiposFromSelection(selectedTipos);
            }
          } catch {
            this.tiposNovedad = [];
            this.tiposNovedadCatalog = [];
            this.allSubtiposCatalog = [];
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.tiposNovedad = [];
          this.tiposNovedadCatalog = [];
          this.allSubtiposCatalog = [];
          this.cdr.markForCheck();
        },
      });
  }

  /** Igual que `tipo-novedad-selector.resolveCatalogoQuery` / `loadTiposNovedad`. */
  private resolveCatalogoQuery$() {
    const listarSinFiltroTipoSolicitud =
      environment.TIPOS_NOVEDAD_LISTAR_SIN_FILTRO_TIPO_SOLICITUD === true;

    if (listarSinFiltroTipoSolicitud) {
      return of(this.buildQueryCatalogoTiposNovedad());
    }

    return this.tipoSolicitudNovedadTrasladoIdService
      .getIdTipoSolicitudNovedadTraslado()
      .pipe(
        map((idTipoSolicitud) =>
          this.buildQueryCatalogoTiposNovedad(idTipoSolicitud),
        ),
      );
  }

  /** Query común GET `tipos-novedad` / `subtipos-novedad` (paso 2). */
  private buildQueryCatalogoTiposNovedad(
    idTipoSolicitud?: number,
  ): CatalogoTiposNovedadQuery {
    const q: CatalogoTiposNovedadQuery = {
      contratoTransitorio: this.esContratoEstadoTransitorio(),
    };
    if (idTipoSolicitud != null && Number.isFinite(Number(idTipoSolicitud))) {
      q.idTipoSolicitud = idTipoSolicitud;
    }
    return q;
  }

  /** RF: contrato «ACTIVO, TRANSITORIO» → `contratoTransitorio=true`. */
  private esContratoEstadoTransitorio(): boolean {
    const e = String(this.estadoContrato ?? '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return e.includes('TRANSITORIO');
  }

  /**
   * Igual que `loadSubtiposNovedad` del paso 2: primero GET por tipo, fallback catálogo anidado.
   */
  private updateSubtiposFromSelection(selectedTipos: string[]): void {
    if (!selectedTipos?.length || !this.catalogoQuery) {
      this.subtiposNovedad = [];
      this.filtrosBusqueda.get('subtipoNovedadTraslado')?.setValue([]);
      this.cdr.markForCheck();
      return;
    }

    const qCat = this.catalogoQuery;
    const requests = selectedTipos.map((nombre) => {
      const codigo = this.resolveCodigoTipoPorNombre(nombre);
      if (!codigo) {
        return of(this.subtiposDesdeCatalogoAnidado('', nombre));
      }

      return this.subtiposNovedadService
        .getSubtiposPorTipoNovedad(codigo, qCat)
        .pipe(
          map((dtos) => {
            if (Array.isArray(dtos) && dtos.length > 0) {
              return this.nombresSubtiposDesdeDto(dtos);
            }
            return this.subtiposDesdeCatalogoAnidado(codigo, nombre);
          }),
          catchError(() =>
            of(this.subtiposDesdeCatalogoAnidado(codigo, nombre)),
          ),
        );
    });

    forkJoin(requests).subscribe({
      next: (arrays) => {
        this.subtiposNovedad = Array.from(new Set(arrays.flat()));
        const currentSubtipos =
          this.filtrosBusqueda.get('subtipoNovedadTraslado')?.value || [];
        const valid = currentSubtipos.filter((v: string) =>
          this.subtiposNovedad.includes(v),
        );
        if (valid.length !== currentSubtipos.length) {
          this.filtrosBusqueda.get('subtipoNovedadTraslado')?.setValue(valid);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.subtiposNovedad = [];
        this.cdr.markForCheck();
      },
    });
  }

  private resolveCodigoTipoPorNombre(nombre: string): string {
    const exact = this.tiposNovedadCatalog.find((t) => t.nombre === nombre);
    if (exact) {
      return exact.codigo;
    }
    const want = this.normalizarNombreCatalogo(nombre);
    const byName = this.tiposNovedadCatalog.find(
      (t) => this.normalizarNombreCatalogo(t.nombre) === want,
    );
    return byName?.codigo ?? '';
  }

  private subtiposDesdeCatalogoAnidado(
    codigoTipo: string,
    nombreTipo: string,
  ): string[] {
    const codNorm = String(codigoTipo).trim();
    let grupo = this.allSubtiposCatalog.find(
      (d) => String(d.idTipoNovedad) === codNorm,
    );
    if (!grupo && nombreTipo) {
      const want = this.normalizarNombreCatalogo(nombreTipo);
      grupo = this.allSubtiposCatalog.find(
        (d) => this.normalizarNombreCatalogo(d.tipoNovedad) === want,
      );
    }
    if (!grupo && nombreTipo) {
      const want = this.normalizarNombreCatalogo(nombreTipo);
      grupo = this.allSubtiposCatalog.find((d) =>
        this.normalizarNombreCatalogo(d.tipoNovedad).includes(want),
      );
    }
    if (!grupo?.subtipos?.length) {
      return [];
    }
    return this.nombresSubtiposDesdeDto(grupo.subtipos);
  }

  private nombresSubtiposDesdeDto(dtos: SubtipoDTO[]): string[] {
    const activos = dtos
      .filter((s) => s && this.esSubtipoEstadoActivo(s.estado))
      .map((s) => String(s.subtipoNovedad ?? '').trim())
      .filter((n) => n.length > 0);
    if (activos.length > 0) {
      return activos;
    }
    return dtos
      .map((s) => String(s.subtipoNovedad ?? '').trim())
      .filter((n) => n.length > 0);
  }

  private esSubtipoEstadoActivo(estado: number | string | undefined): boolean {
    return estado === 1 || estado === '1';
  }

  private normalizarNombreCatalogo(val: string | undefined | null): string {
    return String(val ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  initForm() {
    this.filtrosBusqueda = this.fb.group({
      numeroSolicitud: ['', [Validators.pattern('^[0-9]*$')]],
      tipoNovedadTraslado: [[]],
      subtipoNovedadTraslado: [[]],
    });
  }

  onNumeroSolicitudKeyPress(event: KeyboardEvent): void {
    const tecla = event.key;

    if (!/^\d$/.test(tecla)) {
      event.preventDefault();
    }
  }

  onNumeroSolicitudInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const soloNumeros = input.value.replace(/[^0-9]/g, '');
    if (soloNumeros !== input.value) {
      this.filtrosBusqueda
        .get('numeroSolicitud')
        ?.setValue(soloNumeros, { emitEvent: false });
    }
  }

  onSubmit() {
    const filtros = this.filtrosBusqueda.value;
    const nroSolicitud = filtros.numeroSolicitud?.trim() || '';
    const tipos = filtros.tipoNovedadTraslado || [];
    const subtipos = filtros.subtipoNovedadTraslado || [];

    this.tableData = this.solicitudesOriginales.filter((item) => {
      const coincideSolicitud =
        nroSolicitud === '' || String(item.nroSolicitud).includes(nroSolicitud);
      const coincideTipo =
        tipos.length === 0 || tipos.includes(item.tipoNovedad);
      const coincideSubtipo =
        subtipos.length === 0 || subtipos.includes(item.subtipoNovedad);
      return coincideSolicitud && coincideTipo && coincideSubtipo;
    });
  }

  onReset() {
    this.filtrosBusqueda.reset({
      numeroSolicitud: '',
      tipoNovedadTraslado: [],
      subtipoNovedadTraslado: [],
    });
    this.subtiposNovedad = [];
    this.tableData = [...this.solicitudesOriginales];
  }
}
